import type { AggregateOp } from '@ofmos/shared/field-types';
import { DATE_GRANULARITIES, type DateGranularity } from '@ofmos/shared/record-group';
import { useId } from 'react';
import type { FieldMetadata } from '../../../data/client';
import { cx, SelectMenu, Switch } from '../../../ui';
import { closedDomainOf } from '../emptyGroups';
import {
  aggregateLabel,
  aggregateOptionsFor,
  BUCKET_DROP_REFUSAL,
  dimensionFor,
  GRANULARITY_LABELS,
  groupableColumns,
  isBucketedField,
} from './groupModel';
import { SHEET_LABEL } from './TableSheetControls';
import type { TableColumn } from './tableModel';
import type { GroupingControls } from './useTableControls';

/**
 * THE GROUPED TABLE'S SETTINGS (`docs/decisions/group-by-aggregation.md` D15, D18, D29),
 * written once and placed twice - a popover on the desktop, a sub-page in the phone's
 * Adjust sheet - exactly as the filter builder is.
 *
 * FOUR SETTINGS, IN THE ORDER THEY DEPEND ON EACH OTHER. The dimension comes first because
 * the other three have nothing to attach to without it: a number beside a group header
 * needs a header, a column total is rendered per section and once at the bottom, and
 * "hide empty groups" is about groups. So the last three appear only once the table is
 * grouped, rather than sitting there greyed out teaching nothing.
 *
 * THE ONE CONTROL THAT IS DISABLED RATHER THAN HIDDEN is the toggle, and D29 makes that the
 * rule: for a RELATION or a date bucket there is no closed domain to synthesise the missing
 * groups from - they would be every record of the target object, or every period in an open
 * range - so the toggle cannot do anything, and it says so instead of disappearing. A
 * control that appears and vanishes as the user changes dimension is how a control gets
 * mistrusted; `views/validate.ts` refuses the same state on the way in, so a view written
 * by hand cannot claim what this UI will not produce.
 */
export interface GroupControlsProps {
  readonly columns: readonly TableColumn[];
  /** Only a column that is ON SCREEN can carry a footer under it (D18). */
  readonly shownColumns: readonly TableColumn[];
  readonly grouping: GroupingControls;
  /**
   * WHETHER THE DIMENSION WRITES ITS OWN HEADING (wave 4 round 2, finding 2).
   *
   * The phone's Adjust sheet drills into a page and renders this component under it, so the
   * label is the only word over the pop-up and stays. The desktop's View Options panel gives
   * the section a heading of its own - "Group" - and the label under it was then the second
   * heading on one section, which is what the finding names. `false` drops the visible line
   * and keeps the control's NAME, so a screen reader and every test that asks for "Group by"
   * still find it.
   */
  readonly labelDimension?: boolean | undefined;
}

/** What the desktop button and the phone's drill-in row say without being opened. */
export function describeGroupValue(
  grouping: GroupingControls,
  columns: readonly TableColumn[],
): string {
  const dimension = grouping.groupBy?.[0];
  if (dimension === undefined) return 'None';
  const field = columns.find((column) => column.field.id === dimension.fieldId)?.field;
  if (field === undefined) return 'None';
  return dimension.granularity === undefined
    ? field.label
    : `${field.label}, by ${GRANULARITY_LABELS[dimension.granularity].toLowerCase()}`;
}

const NOT_GROUPED = 'none';
const ROW_COUNT = 'count';

export function GroupControls({
  columns,
  shownColumns,
  grouping,
  labelDimension = true,
}: GroupControlsProps) {
  const dimensionId = useId();
  const granularityId = useId();
  const headerFieldId = useId();
  const headerOpId = useId();

  const candidates = groupableColumns(columns);
  const dimension = grouping.groupBy?.[0];
  const field = candidates.find((column) => column.field.id === dimension?.fieldId)?.field ?? null;

  if (candidates.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        Nothing on this object can be grouped yet. Grouping needs a select, a checkbox, a relation
        or a date field.
      </p>
    );
  }

  const aggregable = columns.filter((column) => aggregateOptionsFor(column.field).length > 0);
  const headerField =
    grouping.groupAggregate?.fieldId === undefined
      ? null
      : (columns.find((column) => column.field.id === grouping.groupAggregate?.fieldId)?.field ??
        null);

  return (
    <div className="space-y-5">
      <section className="flex flex-col gap-2">
        {labelDimension && (
          <label htmlFor={dimensionId} className={SHEET_LABEL}>
            Group by
          </label>
        )}
        <SelectMenu
          id={dimensionId}
          {...(labelDimension ? {} : { ariaLabel: 'Group by' })}
          value={dimension?.fieldId ?? NOT_GROUPED}
          onValueChange={(value) => {
            if (value === NOT_GROUPED) {
              grouping.setGroupBy(null);
              return;
            }
            const chosen = candidates.find((column) => column.field.id === value)?.field;
            if (chosen !== undefined) grouping.setGroupBy([dimensionFor(chosen)]);
          }}
          options={[
            { value: NOT_GROUPED, label: 'None' },
            ...candidates.map((column) => ({
              value: column.field.id,
              label: column.field.label,
            })),
          ]}
        />
      </section>

      {field !== null && isBucketedField(field) && (
        <section className="flex flex-col gap-2">
          <label htmlFor={granularityId} className={SHEET_LABEL}>
            Period
          </label>
          <SelectMenu
            id={granularityId}
            value={dimension?.granularity ?? 'MONTH'}
            onValueChange={(value) =>
              grouping.setGroupBy([dimensionFor(field, value as DateGranularity)])
            }
            options={DATE_GRANULARITIES.map((granularity) => ({
              value: granularity,
              label: GRANULARITY_LABELS[granularity],
            }))}
          />
          {/*
            D13's answer to "whose month is this?", said out loud rather than assumed. The
            zone travels from this browser on every grouped read, with no server default, so
            two people in two countries see two groupings of one saved view - which is
            correct, and is what a stored zone would get wrong the moment somebody travels.
          */}
          {field.type === 'DATETIME' && (
            <p className="text-sm text-ink-muted">
              {`Periods follow this device's time zone, ${Intl.DateTimeFormat().resolvedOptions().timeZone}. Weeks start on Monday.`}
            </p>
          )}
          <p className="text-sm text-ink-muted">{BUCKET_DROP_REFUSAL}</p>
        </section>
      )}

      {field !== null && (
        <>
          <section className="flex flex-col gap-2">
            <label htmlFor={headerFieldId} className={SHEET_LABEL}>
              Show beside each group
            </label>
            <SelectMenu
              id={headerFieldId}
              value={grouping.groupAggregate?.fieldId ?? ROW_COUNT}
              onValueChange={(value) => {
                if (value === ROW_COUNT) {
                  grouping.setGroupAggregate(null);
                  return;
                }
                const chosen = aggregable.find((column) => column.field.id === value)?.field;
                if (chosen === undefined) return;
                // The first operation the field's own registry entry offers, so switching
                // fields never lands on an operation that type does not accept (D12, D14).
                const op = aggregateOptionsFor(chosen)[0];
                if (op !== undefined) grouping.setGroupAggregate({ op, fieldId: chosen.id });
              }}
              options={[
                { value: ROW_COUNT, label: 'The number of records' },
                ...aggregable.map((column) => ({
                  value: column.field.id,
                  label: column.field.label,
                })),
              ]}
            />
            {headerField !== null && grouping.groupAggregate !== null && (
              <SelectMenu
                id={headerOpId}
                ariaLabel={`Calculation for ${headerField.label}`}
                value={grouping.groupAggregate.op}
                onValueChange={(value) =>
                  grouping.setGroupAggregate({ op: value as AggregateOp, fieldId: headerField.id })
                }
                options={aggregateOptionsFor(headerField).map((op) => ({
                  value: op,
                  label: aggregateLabel(op, headerField.type),
                }))}
              />
            )}
          </section>

          {/*
            THE TOGGLE SITS WITH THE OTHER TWO SETTINGS THAT ARE ABOUT GROUPS, above the
            per-column list. It has to be VISIBLE while disabled (D29), and a control that
            can only be found by scrolling past twelve column rows is disabled AND hidden.
          */}
          <section className="flex flex-col gap-1">
            <Switch
              checked={grouping.hideEmptyGroups}
              disabled={closedDomainOf(field) === null}
              onCheckedChange={grouping.setHideEmptyGroups}
              label="Hide groups with no records"
            />
            {closedDomainOf(field) === null && (
              <p className="text-sm text-ink-muted">
                {`Groups with no records cannot be filled in for ${field.label.toLowerCase()}, so there is nothing to hide. A select field or a checkbox has a fixed set of values to complete; a relation or a date does not.`}
              </p>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className={SHEET_LABEL}>Column totals</h3>
            <p className="text-sm text-ink-muted">
              A calculation here is shown under every group and once for the whole table.
            </p>
            {shownColumns
              .filter((column) => aggregateOptionsFor(column.field).length > 0)
              .map((column) => (
                <ColumnTotalRow
                  key={column.field.id}
                  field={column.field}
                  op={grouping.columnAggregates?.[column.field.id] ?? null}
                  onChange={(next) => grouping.setColumnAggregate(column.field.id, next)}
                />
              ))}
          </section>
        </>
      )}
    </div>
  );
}

/*
 * `GroupMenu` STOOD HERE AND HAS NO CALLER LEFT (wave 4, C12). It was the desktop's own
 * popover around `GroupControls`, opened from the "Group" capsule in the list pane's header
 * row; that capsule is gone and the same `GroupControls` is now a section of the window
 * toolbar's view options (`ViewOptions.tsx`), which owns the bound this wrapper used to put
 * around the body. The phone's Adjust sheet renders `GroupControls` directly and always did.
 */

/**
 * ONE COLUMN'S FOOTER OPERATION. A row per column rather than a menu on the footer cell
 * itself: the same control has to work at 375px, where there is no footer row to click.
 */
function ColumnTotalRow({
  field,
  op,
  onChange,
}: {
  readonly field: FieldMetadata;
  readonly op: AggregateOp | null;
  readonly onChange: (next: AggregateOp | null) => void;
}) {
  return (
    <div className={cx('flex items-center justify-between gap-3 py-1')}>
      <span className="min-w-0 flex-1 truncate text-sm text-ink">{field.label}</span>
      <div className="w-40 shrink-0">
        <SelectMenu
          ariaLabel={`Total for ${field.label}`}
          value={op ?? 'none'}
          onValueChange={(value) => onChange(value === 'none' ? null : (value as AggregateOp))}
          options={[
            { value: 'none', label: 'None' },
            ...aggregateOptionsFor(field).map((candidate) => ({
              value: candidate,
              label: aggregateLabel(candidate, field.type),
            })),
          ]}
        />
      </div>
    </div>
  );
}
